import enGB from './locales/en-GB.json';
import enUS from './locales/en-US.json';
import mt from './locales/mt.json';
import tr from './locales/tr.json';
import hi from './locales/hi.json';
import pa from './locales/pa.json';
import es from './locales/es.json';
import fr from './locales/fr.json';
import de from './locales/de.json';

type Locale = 'en-GB' | 'en-US' | 'mt' | 'tr' | 'hi' | 'pa' | 'es' | 'fr' | 'de';

const translations: Record<Locale, any> = {
    'en-GB': enGB,
    'en-US': enUS,
    'mt': mt,
    'tr': tr,
    'hi': hi,
    'pa': pa,
    'es': es,
    'fr': fr,
    'de': de,
};

const DEFAULT_LOCALE: Locale = 'en-GB';
const STORAGE_KEY = 'expensecalc_locale';

class I18nService {
    private currentLocale: Locale = DEFAULT_LOCALE;
    private listeners: Array<() => void> = [];

    constructor() {
        // Load from browser storage
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored && this.isValidLocale(stored)) {
            this.currentLocale = stored as Locale;
        }
    }

    private isValidLocale(locale: string): locale is Locale {
        return locale in translations;
    }

    getLocale(): Locale {
        return this.currentLocale;
    }

    setLocale(locale: Locale, persist: boolean = true) {
        if (!this.isValidLocale(locale)) {
            locale = DEFAULT_LOCALE;
        }
        this.currentLocale = locale;
        if (persist) {
            localStorage.setItem(STORAGE_KEY, locale);
        }
        this.notifyListeners();
    }

    subscribe(listener: () => void) {
        this.listeners.push(listener);
        return () => {
            this.listeners = this.listeners.filter(l => l !== listener);
        };
    }

    private notifyListeners() {
        this.listeners.forEach(listener => listener());
    }

    t(key: string, defaultValue?: string): string {
        const keys = key.split('.');
        let value: any = translations[this.currentLocale];
        
        for (const k of keys) {
            if (value && typeof value === 'object' && k in value) {
                value = value[k];
            } else {
                // Fallback to en-GB if key not found
                value = translations[DEFAULT_LOCALE];
                for (const k2 of keys) {
                    if (value && typeof value === 'object' && k2 in value) {
                        value = value[k2];
                    } else {
                        return defaultValue || key;
                    }
                }
                break;
            }
        }
        
        return typeof value === 'string' ? value : (defaultValue || key);
    }

    getSupportedLocales(): Array<{code: Locale, name: string, flag: string}> {
        return [
            { code: 'en-GB', name: 'English (UK)', flag: '🇬🇧' },
            { code: 'en-US', name: 'English (US)', flag: '🇺🇸' },
            { code: 'mt', name: 'Malti', flag: '🇲🇹' },
            { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
            { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
            { code: 'pa', name: 'ਪੰਜਾਬੀ', flag: '🇮🇳' },
            { code: 'es', name: 'Español', flag: '🇪🇸' },
            { code: 'fr', name: 'Français', flag: '🇫🇷' },
            { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
        ];
    }
}

const i18nService = new I18nService();
export default i18nService;

